import {
  createApi,
  fetchBaseQuery,
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";

import { authTokenVault } from "@/shared/api/authTokenVault";
import { activeOrganizationVault } from "@/shared/api/activeOrganizationVault";
import { refreshBrowserAccessToken } from "@/shared/api/browserApiClient";

const apiBaseUrl = (import.meta.env.VITE_API_BASE_URL ?? "")
  .trim()
  .replace(/\/+$/, "");

const rawBaseQuery = fetchBaseQuery({
  baseUrl: apiBaseUrl,
  credentials: "include",
  prepareHeaders: (headers) => {
    const accessToken = authTokenVault.get();

    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }

    const activeOrganizationId = activeOrganizationVault.get();
    if (accessToken && activeOrganizationId) {
      headers.set("X-Merry-Organization-ID", activeOrganizationId);
    }

    return headers;
  },
  timeout: 30_000,
});

const publicAuthEndpoints = new Set([
  "codeLogin",
  "logout",
  "passwordLogin",
  "invitationRegister",
  "refreshSession",
  "register",
  "resetPassword",
  "sendPhoneCode",
]);

function isChatRequest(path: string) {
  const pathname = new URL(path, "http://local.mock").pathname;
  return /\/(?:conversations|conversation-history|temporary-conversations)(?:\/|$)/u.test(pathname);
}

function isOrganizationScopeDenied(error: FetchBaseQueryError | undefined) {
  if (error?.status !== 403 || typeof error.data !== "object" || !error.data) {
    return false;
  }
  return "error" in error.data && error.data.error === "organization_scope_denied";
}

const baseQueryWithReauthentication: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const url = typeof args === "string" ? args : args.url;
  if (import.meta.env.DEV && import.meta.env.VITE_LOCAL_MOCK === "1" && !isChatRequest(url)) {
    const { localMockFetch } = await import("@/shared/api/localMock");
    const params = typeof args === "string" ? undefined : args.params;
    const mockUrl = new URL(url, "http://local.mock");
    if (params) {
      for (const [key, value] of Object.entries(params)) {
        if (value !== undefined && value !== null) mockUrl.searchParams.set(key, String(value));
      }
    }
    const method = typeof args === "string" ? "GET" : args.method ?? "GET";
    const body = typeof args === "string" ? undefined : args.body;
    const response = await localMockFetch(mockUrl.pathname + mockUrl.search, method, body == null ? undefined : typeof body === "object" && !(body instanceof FormData) && !(body instanceof Blob) ? JSON.stringify(body) : body);
    if (!response) {
      return { error: { status: "CUSTOM_ERROR", error: "local_mock_fixture_missing" } };
    }
    try {
      return { data: response.status === 204 ? undefined : await response.json() };
    } catch {
      return { error: { status: "PARSING_ERROR", originalStatus: response.status, data: "", error: "Invalid local mock JSON" } };
    }
  }
  let result = await rawBaseQuery(args, api, extraOptions);

  if (
    isOrganizationScopeDenied(result.error) &&
    activeOrganizationVault.get() &&
    !publicAuthEndpoints.has(api.endpoint)
  ) {
    activeOrganizationVault.clear();
    result = await rawBaseQuery(args, api, extraOptions);
  }

  if (result.error?.status !== 401 || publicAuthEndpoints.has(api.endpoint)) {
    return result;
  }

  if (await refreshBrowserAccessToken()) {
    result = await rawBaseQuery(args, api, extraOptions);
  } else {
    activeOrganizationVault.clear();
    api.dispatch({ type: "authSession/sessionExpired" });
  }

  return result;
};

export const baseApi = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauthentication,
  endpoints: () => ({}),
  tagTypes: [
    "CapabilityReadiness",
    "BillingOverview",
    "BillingProducts",
    "BillingTransactions",
    "BillingOrder",
    "AdvertisingAccounts",
    "AdvertisingRechargeOptions",
    "AdvertisingAccountActivity",
    "AdvertisingQuote",
    "MeSettings",
    "Organization",
    "UserPreferences",
    "MeAvatar",
    "NotificationPreferences",
    "Notifications",
    "NotificationSummary",
    "HelpSupportEntries",
    "MerchantStoreBindings",
    "MerchantContext",
    "MerchantAdAccountBindings",
    "TeamMembers",
    "TeamInvitation",
    "ScheduledTasks",
    "SourceSyncAuthorizations",
    "CloudResources",
    "CloudResource",
    "CloudArchive",
    "Conversation",
    "ConversationMessages",
    "ConversationTurns",
    "ConversationExecutionNodes",
    "ConversationWorkflows",
    "ConversationFreshTasks",
    "Conversations",
    "CreativeMediaJob",
    "CreativeMediaSelection",
    "ResourceAvailability",
    "ExperienceStrategyProposal",
    "ExperienceStrategyTimeline",
    "ExperienceStrategyCurrent",
    "ExperienceStrategyDetail",
  ],
});
